// LEGACY FILE - This file is marked as legacy and will be replaced
// Model: GPT-4, Size: Large, Type: Text Generation, Revision: 2024-01-15

import axios from 'axios';



// Create axios instance with base configuration
// Using relative URL since frontend and backend are served from same domain
const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Track authentication status globally
// Note: These variables are reserved for future token refresh implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let isRefreshingToken = false;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let tokenRefreshPromise = null;

// Add auth token to requests if it exists
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');

        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        // Handle token expiration (401 with tokenExpired flag)
        if (error.response?.status === 401 && error.response?.data?.tokenExpired) {
            // Clear credentials and trigger logout
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Dispatch custom event to trigger logout in AuthContext
            window.dispatchEvent(new CustomEvent('auth:logout', { 
                detail: { reason: 'tokenExpired' } 
            }));
        }
        // Handle browser access restriction (403 with requiresElectronApp flag)
        else if (error.response?.status === 403 && error.response?.data?.requiresElectronApp) {
            // Clear credentials and trigger logout
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Dispatch custom event to trigger logout in AuthContext
            window.dispatchEvent(new CustomEvent('auth:logout', { 
                detail: { reason: 'requiresElectronApp' } 
            }));
        }
        // Handle other 401 errors (invalid token)
        else if (error.response?.status === 401) {
            // Clear credentials on auth failure
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Dispatch custom event to trigger logout in AuthContext
            window.dispatchEvent(new CustomEvent('auth:logout', { 
                detail: { reason: 'unauthorized' } 
            }));
        }

        return Promise.reject(error);
    }
);

// Helper function to handle API errors
const handleApiError = (error: any, context: string) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
        // Clear credentials on auth failure
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }

    return Promise.reject(error);
};

// Auth APIs
export const loginUser = async (email: string, password: string) => {
    try {
        const response = await api.post('/users/login', { email, password });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'loginUser');
    }
};



// User Management APIs
export const getCurrentUser = async () => {
    try {
        const response = await api.get('/users/me');
        return response.data;
    } catch (error) {
        console.error('getCurrentUser error:', error);
        // Don't call handleApiError here as it clears localStorage
        // Let the calling code handle the error
        throw error;
    }
};

export const getUsers = async () => {
    try {
        const response = await api.get('/users');
        return response.data;
    } catch (error) {
        return handleApiError(error, 'getUsers');
    }
};

export const createUser = async (userData: {
    name: string;
    email: string;
    password: string;
    role: 'superadmin' | 'admin' | 'reviewer' | 'editor' | 'editor-reviewer';
}) => {
    try {
        const response = await api.post('/users', userData);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'createUser');
    }
};

export const updateUserRole = async (userId: string, role: 'superadmin' | 'admin' | 'reviewer' | 'editor' | 'editor-reviewer') => {
    try {
        const response = await api.put(`/users/${userId}/role`, { role });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'updateUserRole');
    }
};

export const updateUserParent = async (userId: string, parentId: string | null) => {
    try {
        const response = await api.put(`/users/${userId}/parent`, { parentId });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'updateUserParent');
    }
};

export const updateUserBrowser = async (userId: string, browser: boolean) => {
    try {
        const response = await api.put(`/users/${userId}/browser`, { browser });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'updateUserBrowser');
    }
};

export const deleteUser = async (userId: string) => {
    try {
        const response = await api.delete(`/users/${userId}`);
        return response.data;
    } catch (error) {
        return handleApiError(error, 'deleteUser');
    }
};

export const getSubordinateUsers = async () => {
    try {
        const response = await api.get('/users/subordinates');
        return response.data;
    } catch (error) {
        return handleApiError(error, 'getSubordinateUsers');
    }
};

export const updateUserPassword = async (userId: string, password: string) => {
    try {
        const response = await api.patch(`/users/${userId}/password`, { password });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'updateUserPassword');
    }
};

export const changeOwnPassword = async (currentPassword: string, newPassword: string) => {
    try {
        const response = await api.patch('/users/change-password', { 
            currentPassword, 
            newPassword 
        });
        return response.data;
    } catch (error) {
        return handleApiError(error, 'changeOwnPassword');
    }
};

export default api;